import {
  useEffect, useState,
} from 'react';

// Third party
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';

// Components
import { Crisp } from 'crisp-sdk-web';
import { useQueryClient } from '@tanstack/react-query';
import { setSessionVal, getSessionValue } from '@mgm/common/utils';
import { BaseLabel, GenericHeader } from '@mgm/common/components/base';
import { WelcomeWrapper } from '@mgm/common/wrappers';
import { PasswordField } from '@mgm/common/components/form';
import { LanguageSelect } from '../../../components';
import { TCustomFieldOptions, TFormInputs } from '../../../components/form';
import LoginActions from './LoginActions';

// Hooks
import { useLoginAPI } from '../../../hooks/api';

import { useCommon, useQueryParams } from '../../../hooks/useCommon';
import { persistor } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getPasswordValidation } from '../../../store/common/selectors';
import { commonActions } from '../../../store/common/slice';

const Login = () => {
  const dispatch = useAppDispatch();
  const passwordValidation = useAppSelector(getPasswordValidation);

  const {
    mutate: loginMutate,
    isLoading,
  } = useLoginAPI();

  const query = useQueryParams();
  const { handleLogout } = useCommon();
  const queryClient = useQueryClient();

  const initPasswordValidation = () => {
    Object.keys(passwordValidation).forEach((key) => {
      dispatch(commonActions.setPasswordValidation({
        key,
        value: null,
      }));
    });
  };

  useEffect(() => {
    const email = query.get('email');
    const password = query.get('access');
    const resetCache = query.get('cc');
    const from = query.get('form');
    const goTo = query.get('goTo');

    if (resetCache) {
      handleLogout();
    }

    const redirectTo = goTo === 'schedulesWeekly' ? 'schedules/weekly' : goTo;

    if (email && password) {
      const data = {
        email,
        password,
        isRedirect: true,
        isDisplayToast: false,
        isHashPassword: false,
        isAdmin: true,
        ...(goTo && {
          redirectTo: `/${redirectTo}?from=${from}`,
        }),
      };

      persistor.purge();
      queryClient.removeQueries();
      sessionStorage.removeItem('MM_AUTH');
      localStorage.clear();
      Crisp.setTokenId('');

      loginMutate(data);
    }

    const userData = getSessionValue('user') as { email: string; password: string };

    if (userData?.email) {
      const data = {
        email: userData?.email,
        password: userData.password,
        isRedirect: true,
        isDisplayToast: true,
        ...(goTo && {
          redirectTo: `/${redirectTo}?from=${from}`,
        }),
      };

      loginMutate(data);
    }
  }, []);

  const { t } = useTranslation(['welcome', 'inputs']);

  const methods = useForm<Pick<TFormInputs, TCustomFieldOptions>>({
    mode: 'all',
  });

  const [email, setEmail] = useState('');

  const [rememberMe, setRememberMe] = useState(false);

  const loginToPlatform = (formData: Pick<TFormInputs, 'password'>) => {
    if (rememberMe) {
      setSessionVal('user', {
        email: email ?? '',
        password: formData.password,
      });
    }

    const from = query?.get('form');
    const goTo = query?.get('goTo');

    const redirectTo = goTo === 'schedulesWeekly' ? 'schedules/weekly' : goTo;

    const data = {
      email: email ?? '',
      password: formData.password ?? '',
      isRedirect: true,
      isDisplayToast: true,
      ...(goTo && {
        redirectTo: `/${redirectTo}?from=${from}`,
      }),
    };

    loginMutate(data);
  };

  const isEmbedded = window !== window.parent;

  const content = (
    <>
      <LanguageSelect />
      <GenericHeader
        subtitleProps={{
          children: t('loginTitle'),
        }}
        titleProps={{
          children: t('login'),
        }}
      />
      <form
        noValidate
        onSubmit={methods.handleSubmit(loginToPlatform)}
      >
        <Stack spacing={3}>
          <FormProvider {...methods}>
            <Box>
              <BaseLabel letterSpacing="-0.5px">{t('email', { ns: 'inputs' })}</BaseLabel>
              <TextField
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                id="email"
                name="email"
                size="small"
                autoComplete="email"
                placeholder={t('enterEmail', { ns: 'inputs' })}
                autoFocus
              />
            </Box>
            <PasswordField
              initPasswordValidation={initPasswordValidation}
              name="password"
              labelText={t('password', { ns: 'inputs' })}
              placeholder={t('enterPassword', { ns: 'inputs' })}
              isRequired
            />
          </FormProvider>
        </Stack>
        <Box paddingY={2}>
          <LoginActions setRemember={setRememberMe} rememberMe={rememberMe} />
          <LoadingButton
            loading={isLoading}
            disabled={!methods.formState.isValid || !email || isLoading}
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
          >
            {t('login')}
          </LoadingButton>
        </Box>
      </form>
    </>
  );

  return isEmbedded ? content : <WelcomeWrapper>{content}</WelcomeWrapper>;
};

export {
  Login,
};
